import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTenantStore } from 'src/enviroment/store/tenentStore'
import { client } from '../axios'
import { PaymentMethod } from '@stripe/stripe-js'

export enum PaymentMethods {
  List = 'PaymentMethodsList',
  Setup = 'PaymentMethodsSetup',
  Save = 'PaymentMethodsSave',
  Delete = 'PaymentMethodsDelete',
  MakeDefault = 'PaymentMethodsMakeDefault',
}

export interface IPaymentMethod {
  id: string
  brand: 'visa' | 'mastercard'
  last4: string
  expMonth: number
  expYear: number
  isDefault: boolean
}

interface SetupIntentResponse {
  clientSecret: string
}

interface SavePaymentMethodResponse {
  success: boolean
}

export type PaymentMethodsResponse = IPaymentMethod[]

export function useGetPaymentMethods() {
  const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

  return useQuery({
    queryKey: [PaymentMethods.List, selectedTenantId],
    queryFn: async () => {
      const response = await client.get<PaymentMethodsResponse>(`/tenants/${selectedTenantId}/payment-method/list`)
      return response.data
    },
    enabled: Boolean(selectedTenantId),
  })
}

export function useCreateSetupIntent() {
  const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

  return useMutation({
    mutationKey: [PaymentMethods.Setup],
    mutationFn: async () => {
      const response = await client.get<SetupIntentResponse>(
        `/tenants/${selectedTenantId}/payment-method/create-setup-intent`,
      )
      console.log(response)
      return response.data
    },
  })
}

export function useSavePaymentMethod() {
  const queryClient = useQueryClient()
  const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

  return useMutation({
    mutationKey: [PaymentMethods.Save],
    mutationFn: async (paymentMethodId: string | null | PaymentMethod) => {
      const response = await client.post<SavePaymentMethodResponse>(
        `/tenants/${selectedTenantId}/payment-method/save-payment-method`,
        { paymentMethodId },
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PaymentMethods.List, selectedTenantId],
      })
    },
  })
}

export function useDeletePaymentMethod() {
  const queryClient = useQueryClient()
  const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

  return useMutation({
    mutationKey: [PaymentMethods.Delete],
    mutationFn: async (paymentMethodId: string) => {
      const response = await client.delete<{ success: boolean }>(
        `/tenants/${selectedTenantId}/payment-method/${paymentMethodId}`,
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PaymentMethods.List, selectedTenantId],
      })
    },
  })
}

export function useMakeDefaultPaymentMethod() {
  const queryClient = useQueryClient()
  const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

  return useMutation({
    mutationKey: [PaymentMethods.MakeDefault],
    mutationFn: async (paymentMethodId: string) => {
      const response = await client.post<{ success: boolean }>(
        `/tenants/${selectedTenantId}/payment-method/${paymentMethodId}/make-default`,
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PaymentMethods.List, selectedTenantId],
      })
    },
  })
}
